import { useResidentList } from '@/hooks/useResidentList';
import { ResidentListStatus } from '@/components/residentListStatus';
import { normalizeResidentSortType } from '@/utils/residentSort';
import React from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import api from '@/api';
import { ResidentListHeader } from '@/components/residentListHeader';
import { formatUtcFetchTimestamp } from '@/utils/dateutil';
import { residentListSettingsSelector, residentListSettingsSlice } from '@/redux/config/residentListSettings';
import { navSelector, navSlice } from '@/redux/config/leftnav';
import { selfDataSelector } from '@/redux/config/selfData';
import { BatteryAlertModal } from '@/components/batteryAlertModal';
import { useFetchDFreeGraphList } from '@/hooks/multi/useFetchDFreeGraphList';
import { IMdResident } from '@/types/api/medical/getResidents';
import { color } from '@/styles';
import { DFREE_GRAPH_VIEW_TYPE } from '@/constants';
import { Button } from '@mui/material';
import { isValueView } from '@/utils/dfreeGraph';
import { RecordDialog } from '@/components/careRecord/recordDialog';
import { MedicalRecordModal } from '@/components/careRecord/medicalRecordModal';
import DfreeGraphListItem from './dfreeGraphListItem';

const Container = css`
  padding: 0 16px;
`;

const SwitchDisplayButton = css`
  display: flex;
  align-items: center;
  column-gap: 4px;
`;

const BatteryAlertModalMessage = css`
  color: ${color.dark_gray};
`;

export const MedicalDfreeGraphList: React.FC = () => {
  const dispatch = useDispatch();
  const { nursingHomeId } = useParams<{ nursingHomeId: string }>();
  const { badge } = useSelector(navSelector);
  const residentListSettings = useSelector(residentListSettingsSelector);
  const self = useSelector(selfDataSelector);
  const [resident, setResident] = React.useState<IMdResident>();
  const effectiveSort = normalizeResidentSortType(residentListSettings.sortType, true);
  const permittedUnitId = self.selfUser.hasUnitPerm ? self.selfUser.nursingHomeUnitId : null;
  const requestScope = JSON.stringify([self.selfUser.id, nursingHomeId, permittedUnitId, self.selfUser.serviceType]);
  const { fetchMdDFreeGrapListData } = useFetchDFreeGraphList();
  const [batteryAlertResident, setBatteryAlertResident] = React.useState<IMdResident | null>(null);

  const { t } = useTranslation();

  const { data, residents: sortedResidents, status: fetchStatus, refresh: fetchResidents } = useResidentList<
    IMdResident, { residents: IMdResident[]; dateRange: { start: string; end: string } }
  >({
    scope: requestScope,
    enabled: !!self.selfUser.id,
    categories: residentListSettings.categories,
    sortType: effectiveSort,
    permittedUnitId,
    fetchData: async () => {
      const dateRange = {
        start: formatUtcFetchTimestamp(dayjs().subtract(5, 'hour')),
        end: formatUtcFetchTimestamp(dayjs()),
      };
      const residents = await fetchMdDFreeGrapListData(dateRange.start, dateRange.end);
      return { residents, dateRange };
    },
    onLoaded: (residents) => {
      setBatteryAlertResident(residents.find((v) => v.batteryAlertNotification) ?? null);
    },
  });

  const UpdateBatteryAlert = async (residentId?: number) => {
    setBatteryAlertResident(null);
    if (residentId) {
      await api.put('/residents/:residentId/notifications/covers', {
        residentId: residentId,
        uncaredBatteryAlert: false,
      });
      fetchResidents();
    }
  };

  const handleOpenRecordModal = React.useCallback((resident: IMdResident) => {
    setResident(resident);
  }, []);

  const handleCloseRecordModal = React.useCallback(() => {
    setResident(undefined);
  }, []);

  const handleUpdate = React.useCallback(() => {
    fetchResidents();
  }, [fetchResidents]);

  // 通知アラートの消去とバッヂカウントの更新処理
  const handleResetNotification = React.useCallback(
    async (residentId: number, coverTypes?: string[]) => {
      let params = { residentId } as {
        residentId: number;
        uncaredBigBladder?: boolean;
        uncaredUrination?: boolean;
        uncaredGasPooped?: boolean;
        uncaredGasPrediction?: boolean;
        uncaredGotUp?: boolean;
        uncaredSensorError?: boolean;
        uncaredUrineReset?: boolean;
        uncaredZeroReset?: boolean;
        uncaredUpsideDown?: boolean;
        uncaredMoveUpperAdvice?: boolean;
        uncaredMoveLowerAdvice?: boolean;
      };

      const coverTypeMap: Record<string, keyof typeof params> = {
        bigBladder: 'uncaredBigBladder',
        urination: 'uncaredUrination',
        gasPooped: 'uncaredGasPooped',
        gasPrediction: 'uncaredGasPrediction',
        gotUp: 'uncaredGotUp',
        sensorError: 'uncaredSensorError',
        urineReset: 'uncaredUrineReset',
        zeroReset: 'uncaredZeroReset',
        upsideDown: 'uncaredUpsideDown',
        moveUpperAdvice: 'uncaredMoveUpperAdvice',
        moveLowerAdvice: 'uncaredMoveLowerAdvice',
      };

      const selectedCoverTypes = coverTypes ?? Object.keys(coverTypeMap);
      params = selectedCoverTypes.reduce((acc, type) => {
        const paramKey = coverTypeMap[type] as keyof typeof params;
        return paramKey ? { ...acc, [paramKey]: false } : acc;
      }, params);

      await api.put('/residents/:residentId/notifications/covers', params);
      if (badge.resident > 0) {
        dispatch(navSlice.actions.setResidentBadge(-1));
      }
      fetchResidents();
    },
    [fetchResidents, badge, dispatch]
  );

  return (
    <div css={Container}>
      <ResidentListHeader
        title={t('dfreeGraph', 'DFreeグラフ')}
        categories={residentListSettings.categories}
        sortType={residentListSettings.sortType}
        onUpdate={handleUpdate}
        rightElement={
          <Button
            onClick={() =>
              dispatch(
                residentListSettingsSlice.actions.setViewType(
                  isValueView(residentListSettings.viewType)
                    ? DFREE_GRAPH_VIEW_TYPE.Volume
                    : DFREE_GRAPH_VIEW_TYPE.Value
                )
              )
            }
            css={SwitchDisplayButton}
          >
            <span style={{ color: color.black }}>{t('SwitchDisplay', '表示切替')}</span>
            <img src='/images/icons/switch-display.svg' width='18' height='18' alt={t('SwitchDisplay', '表示切替')} />
          </Button>
        }
      />
      {data && sortedResidents.length > 0 ? (
        <>
          {sortedResidents.map((resident, i) => (
            <DfreeGraphListItem
              key={resident.id}
              resident={resident}
              dateRange={data.dateRange}
              viewType={residentListSettings.viewType}
              openRecordModal={() => handleOpenRecordModal(resident)}
              resetNotification={(coverType?: string) =>
                handleResetNotification(resident.id, coverType ? [coverType] : undefined)
              }
            />
          ))}
        </>
      ) : (
        <ResidentListStatus status={fetchStatus} />
      )}
      {resident && (
        <RecordDialog isOpen={!!resident} dialogWidth={'640px'} onClose={handleCloseRecordModal}>
          <MedicalRecordModal
            resident={resident}
            resetNotification={handleResetNotification}
            onClose={handleCloseRecordModal}
            onUpdate={handleUpdate}
          />
        </RecordDialog>
      )}
      <BatteryAlertModal
        isOpen={!!batteryAlertResident}
        name={batteryAlertResident?.name}
        title={t('anAbnormalityHasBeenDetectedInTheDFreeDevice', 'DFree機器本体の異常を検知しました。')}
        onClose={() => {
          UpdateBatteryAlert(batteryAlertResident?.id);
        }}
      >
        <div css={BatteryAlertModalMessage}>
          {t('RemoveDFreeFromYourBodyAndTurnItOff', 'DFreeを身体から取り外し、電源を切ってください。')}
        </div>
      </BatteryAlertModal>
    </div>
  );
};
